/* Notifications — client half. Two delivery paths, same plan (shared/prayer.ts):

   1. In-page: the day's plan is posted to the service worker, which fires
      timers while the app is open/recently backgrounded.
   2. Server push: the device registers its push subscription + prefs with
      /api/push; push-worker rebuilds the plan from those prefs every minute and
      sends what's due, so notifications arrive with the app fully closed.

   The SW de-duplicates by tag, so whichever arrives first wins.

   Devices authenticate with a random id + secret generated here (no account
   needed). The pair is mirrored into Cache Storage so the service worker can
   re-register a rotated subscription on its own (pushsubscriptionchange). */
import { useEffect } from 'react';
import { SALAH, addDays, buildDayPlan, dayKeyIn } from '@shared/prayer';
import { useProgress, useSettings, alive } from './store';
import { pushNeedsInstall, pushSupported } from './ios';
import { create } from 'zustand';
import { goalProgress, secondsOn } from '@/features/progress/stats';
import { periodOn } from '@/features/fasting/fastMetrics';

/** Prayers already logged on a day (any mark), or all five on a period day when none is due —
    so the "running late" reminder isn't sent for them. */
function prayedOn(dayKey: string, today: string): string[] {
  const { prayers, periods } = useProgress.getState();
  if (useSettings.getState().settings.gender === 'female' && periodOn(periods, dayKey, today)) return [...SALAH];
  const d = prayers[dayKey];
  return d && !d.deleted ? SALAH.filter((k) => d.marks[k]) : [];
}

/* Must match public/sw.js and push-worker/src/index.ts (tests/push.spec.ts). */
export const VAPID_PUBLIC_KEY = 'BGvWELuX2zjJJL1HVqNq4iPVTvKq3AbnKZ_B25irY0ZkXGHHxvX8WJf1Uw0OH1YbPN8GFzvnc3_vHNwToyOKrf0';

const AUTH_CACHE = 'ftu-auth';
const DEVICE_URL = '/__push_device';
const LS_DEVICE = 'ftu_push_device';
const LS_FP = 'ftu_push_fp';
const HEARTBEAT_MS = 6 * 3600_000;

const b64ToBytes = (b64: string) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

function device(): { id: string; secret: string } {
  try {
    const d = JSON.parse(localStorage.getItem(LS_DEVICE) || 'null');
    if (d?.id && d?.secret) return d;
  } catch {}
  const rnd = (n: number) => Array.from(crypto.getRandomValues(new Uint8Array(n)), (b) => b.toString(16).padStart(2, '0')).join('');
  const d = { id: rnd(16), secret: rnd(32) };
  try { localStorage.setItem(LS_DEVICE, JSON.stringify(d)); } catch {}
  return d;
}

async function mirrorDeviceToSW(d: { id: string; secret: string }) {
  try { await (await caches.open(AUTH_CACHE)).put(DEVICE_URL, new Response(JSON.stringify(d), { headers: { 'Content-Type': 'application/json' } })); } catch {}
}

export type PushState = 'unsupported' | 'needs-install' | 'default' | 'denied' | 'granted';

export function pushState(): PushState {
  if (pushNeedsInstall()) return 'needs-install';
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as PushState;
}

/** Must be called from a user gesture (iOS requires it for the permission prompt). */
export async function enablePush(): Promise<PushState> {
  if (pushNeedsInstall()) return 'needs-install';
  if (!pushSupported()) return 'unsupported';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm as PushState;
  await syncPush(true);
  return 'granted';
}

async function subscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with an old VAPID key can never receive our pushes.
  if (sub) {
    const key = sub.options?.applicationServerKey;
    if (key && btoa(String.fromCharCode(...new Uint8Array(key))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') !== VAPID_PUBLIC_KEY) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(VAPID_PUBLIC_KEY) });
  return sub;
}

/** Register/refresh with the server. Skips the POST when nothing changed, except a 6-hourly heartbeat. */
export async function syncPush(force = false) {
  if (pushState() !== 'granted') return;
  const sub = await subscription();
  if (!sub) return;
  const d = device();
  await mirrorDeviceToSW(d);
  const { prayer, notify, hijriOffset } = useSettings.getState().settings;
  /* The server has no view of local progress, so tell it when today's goal is
     already met — the worker then skips the evening goal reminder. */
  let goalMetOn: string | null = null;
  try { goalMetOn = localStorage.getItem('ftu_goal_met'); } catch {}
  // …and which prayers are already logged, so it skips their "running late" reminder.
  const today = dayKeyIn(prayer.tz), yesterday = addDays(today, -1);
  const prayed = { [yesterday]: prayedOn(yesterday, today), [today]: prayedOn(today, today) };
  const body = { id: d.id, secret: d.secret, subscription: sub.toJSON(), prefs: { prayer, notify, goalMetOn, hijriOffset, prayed } };
  const fp = JSON.stringify([sub.endpoint, prayer, notify, goalMetOn, hijriOffset, prayed]);
  try {
    const prev = JSON.parse(localStorage.getItem(LS_FP) || 'null');
    if (!force && prev?.fp === fp && Date.now() - prev.at < HEARTBEAT_MS) return;
  } catch {}
  const r = await fetch('/api/push', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (r.ok) try { localStorage.setItem(LS_FP, JSON.stringify({ fp, at: Date.now() })); } catch {}
}

export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    await sub?.unsubscribe();
    const d = device();
    await fetch('/api/push', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: d.id, secret: d.secret }) });
    localStorage.removeItem(LS_FP);
  } catch {}
}

/** One delivery attempt. status: push service HTTP code; 0 network error; -1 too late, not sent. */
export interface PushLogEntry { tag: string; kind: string; title: string; fireAt: number; sentAt: number; status: number; receivedAt: number | null }
export interface PushServerStatus { registered: boolean; updatedAt?: number; delivered?: number; dead?: boolean; deadAt?: number | null; nextDue?: number; testPending?: boolean; workerAt?: number; workerError?: string | null; log?: PushLogEntry[] }

/** Shown when the app reconnects after notifications had stopped — so it's never silent. */
export const usePushAlert = create<{ stoppedAt: number | null; dismiss: () => void }>((set) => ({ stoppedAt: null, dismiss: () => set({ stoppedAt: null }) }));

/** The Cron Worker runs every minute; older than this means it has stopped. */
export const WORKER_STALE_MS = 5 * 60_000;

export async function pushServerStatus(): Promise<PushServerStatus | null> {
  try {
    const d = device();
    const r = await fetch('/api/push/status', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(d) });
    return r.ok ? r.json() : null;
  } catch { return null; }
}

/** Ask the server to push to this device on its next tick (≤ ~1 min) — tests the closed-app path end to end. */
export async function serverTestNotification(): Promise<boolean> {
  try {
    const d = device();
    const r = await fetch('/api/push/test', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(d) });
    return r.ok;
  } catch { return false; }
}

/* Self-healing, run when the app opens or returns to the foreground (at most
   hourly). Push breaks silently: Safari never fires pushsubscriptionchange,
   and an expired endpoint still looks healthy to the page. The server knows,
   so ask it:
   - not registered (row purged, or never reached it) → register again;
   - endpoint dead (push service said 404/410) → drop the browser's
     subscription, create a fresh one and register that. */
const LS_HEAL = 'ftu_push_heal';
export async function healPush(force = false): Promise<PushServerStatus | null> {
  if (pushState() !== 'granted') return null;
  try {
    if (!force && Date.now() - Number(localStorage.getItem(LS_HEAL) || 0) < 3600_000) return null;
    localStorage.setItem(LS_HEAL, String(Date.now()));
  } catch {}
  const status = await pushServerStatus();
  if (!status) return null; // offline or server error — try again next time
  if (status.dead) {
    try { await (await (await navigator.serviceWorker.ready).pushManager.getSubscription())?.unsubscribe(); } catch {}
  }
  if (!status.registered || status.dead) {
    // Had this device been registered before? Then notifications really had stopped — say so.
    let wasRegistered = false;
    try { wasRegistered = !!localStorage.getItem(LS_FP); } catch {}
    if (status.dead || wasRegistered) usePushAlert.setState({ stoppedAt: status.deadAt ?? Date.now() });
    await syncPush(true).catch(() => {});
    return pushServerStatus();
  }
  return status;
}

export async function testNotification() {
  const reg = await navigator.serviceWorker.ready;
  await reg.showNotification('For The Ummah', { body: 'Notifications are working — jazāk Allāhu khayran.', icon: '/icons/icon-192.png', tag: 'ftu-test' });
}

/** Keeps the in-page plan and the server registration up to date. Mount once. */
export function useNotificationSync() {
  const prayer = useSettings((s) => s.settings.prayer);
  const notify = useSettings((s) => s.settings.notify);
  const sessions = useProgress((s) => s.sessions);
  const goals = useProgress((s) => s.goals);
  const prayers = useProgress((s) => s.prayers);
  const periods = useProgress((s) => s.periods);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const today = dayKeyIn(prayer.tz);
    const ss = alive(sessions);
    const goal = alive(goals).find((g) => g.active);
    const met = goal ? goalProgress(goal, ss, today).today >= 1 : secondsOn(ss, today) / 60 >= useSettings.getState().settings.dailyGoalMinutes;
    if (met) try { localStorage.setItem('ftu_goal_met', today); } catch {}
    const ho = useSettings.getState().settings.hijriOffset;
    const tomorrow = addDays(today, 1);
    const plan = [...buildDayPlan(prayer, notify, today, met, ho, prayedOn(today, today)), ...buildDayPlan(prayer, notify, tomorrow, false, ho, prayedOn(tomorrow, today))];
    navigator.serviceWorker.ready.then((reg) => reg.active?.postMessage({ type: 'SCHEDULE_NOTIFICATIONS', notifications: plan }));
    void syncPush().catch(() => {});
  }, [prayer, notify, sessions, goals, prayers, periods]);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || typeof Notification === 'undefined') return;
    const heal = () => { if (document.visibilityState === 'visible') void healPush().catch(() => {}); };
    heal();
    document.addEventListener('visibilitychange', heal);
    return () => document.removeEventListener('visibilitychange', heal);
  }, []);
}
